import { inject, Injectable, signal, computed } from '@angular/core';
import { ContactoPersona } from '../../../../domain/model/persona/contacto-persona';
import { ContactoPersonaRepository } from '../../../../domain/service/persona/contacto-persona-repository';
import { MessageService } from 'primeng/api';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS
} from 'src/app/common/constants-ejb';
import { CursorPageResponse } from '../../../../domain/model/cumplimiento/cumplimiento';
import { DireccionCursor } from '../../../../domain/model/common/page-response.dto';

@Injectable()
export class BuscadorContactosFacade {
  private readonly repo = inject(ContactoPersonaRepository);
  private readonly messageService = inject(MessageService);

  contactos = signal<ContactoPersona[]>([]);
  loading = signal(false);
  /** true mientras un guardado está en curso (evita doble envío) */
  guardando = signal(false);
  /** Resultado del último saveContact: el contenedor solo cierra el diálogo si fue exitoso */
  ultimoGuardadoExitoso = false;
  totalRecords = signal(0);

  // pagination
  pageSize = signal<number>(DEFAULT_ROWS_PER_PAGE);
  pageSizeOptions = PAGE_SIZE_OPTIONS.map((v) => ({
    label: String(v),
    value: v
  }));
  currentPage = signal(1);
  first = signal(0);

  // Cursor-based pagination state
  private lastPageResponse: CursorPageResponse<ContactoPersona> | null = null;
  // Última petición que produjo la página visible: permite recargarla tras guardar
  private lastRequest: {
    cursor: string | null;
    direction: DireccionCursor;
  } | null = null;

  // Expose simple flags for the presentational component to decide pagination controls
  get hasNext(): boolean {
    return !!this.lastPageResponse?.hasNext;
  }

  get hasPrevious(): boolean {
    return !!this.lastPageResponse?.hasPrevious;
  }
  // last params passed to abrir
  params: {
    cdpersona?: number;
    nmconsecCentroTrabajo?: number;
    nmconsecOrden?: number;
  } | null = null;

  readonly totalPages = computed(() => {
    const total = this.totalRecords();
    const size = this.pageSize();
    return total > 0 ? Math.ceil(total / size) : 0;
  });

  async abrir(params?: {
    cdpersona?: number;
    nmconsecCentroTrabajo?: number;
    nmconsecOrden?: number;
  }) {
    this.params = params ?? null;
    this.lastRequest = null;
    this.first.set(0);
    this.currentPage.set(1);
    await this.loadPage(0, this.pageSize());
  }

  cerrar() {
    this.params = null;
    this.contactos.set([]);
    this.totalRecords.set(0);
    this.loading.set(false);
  }

  /**
   * Consulta UNA página al backend (keyset/cursor): por centro de trabajo si viene en los
   * parámetros; si no, por persona (solo activos). Legacy: listarContactosBenefOrden.
   */
  private consultarPagina(
    cursor: string | null,
    size: number,
    direction: DireccionCursor
  ) {
    this.lastRequest = { cursor, direction };
    // Legacy (SearchBenefContacts desde el cumplimiento): listarContactosBenefOrden con la orden
    if (this.params?.nmconsecOrden) {
      return this.repo.getByOrdenPaged(this.params.nmconsecOrden, cursor, size, direction);
    }
    if (this.params?.nmconsecCentroTrabajo) {
      return this.repo.getByCentroTrabajoPaged(
        this.params.nmconsecCentroTrabajo,
        cursor,
        size,
        direction
      );
    }
    return this.repo.getByPersonaPaged(
      this.params!.cdpersona!,
      true,
      cursor,
      size,
      direction
    );
  }

  async loadPage(start: number, length: number): Promise<void> {
    this.loading.set(true);
    try {
      let items: ContactoPersona[] = [];
      let total = 0;
      if (
        this.params?.nmconsecOrden ||
        this.params?.nmconsecCentroTrabajo ||
        this.params?.cdpersona
      ) {
        // Cursor-based navigation: determine requested page index
        const requestedPage = Math.floor(start / length) + 1;

        // Helper to normalize response
        const normalize = (resp: any): CursorPageResponse<ContactoPersona> => {
          if (resp && resp.content)
            return resp as CursorPageResponse<ContactoPersona>;
          // legacy shape
          if (resp && resp.items) {
            return {
              content: resp.items,
              size: length,
              nextCursor: null,
              previousCursor: null,
              hasNext: false,
              hasPrevious: false,
              totalElements: resp.total
            } as CursorPageResponse<ContactoPersona>;
          }
          if (Array.isArray(resp)) {
            return {
              content: resp,
              size: length,
              nextCursor: null,
              previousCursor: null,
              hasNext: false,
              hasPrevious: false,
              totalElements: resp.length
            } as CursorPageResponse<ContactoPersona>;
          }
          return {
            content: [],
            size: length,
            nextCursor: null,
            previousCursor: null,
            hasNext: false,
            hasPrevious: false
          } as CursorPageResponse<ContactoPersona>;
        };

        // Fetch logic
        let pageResp: CursorPageResponse<ContactoPersona> | null = null;

        // If requesting first page
        if (requestedPage === 1) {
          const resp = await this.consultarPagina(
            null,
            length,
            'NEXT'
          );
          pageResp = normalize(resp);
          this.lastPageResponse = pageResp;
          this.currentPage.set(1);
        } else {
          // If user navigates sequentially forward/backward we can use stored cursors
          const cur = this.currentPage();

          if (requestedPage === cur + 1) {
            // next
            const cursor = this.lastPageResponse?.nextCursor ?? null;
            const resp = await this.consultarPagina(
            cursor,
              length,
              'NEXT'
            );
            pageResp = normalize(resp);
            this.lastPageResponse = pageResp;
            this.currentPage.set(cur + 1);
          } else if (requestedPage === cur - 1) {
            // previous
            const cursor = this.lastPageResponse?.previousCursor ?? null;
            const resp = await this.consultarPagina(
            cursor,
              length,
              'PREVIOUS'
            );
            pageResp = normalize(resp);
            this.lastPageResponse = pageResp;
            this.currentPage.set(cur - 1);
          } else if (requestedPage === cur && this.lastRequest) {
            // misma página (recarga tras guardar): repetir la petición que la produjo
            const { direction } = this.lastRequest;
            const resp = await this.consultarPagina(
              this.lastRequest.cursor,
              length,
              direction
            );
            pageResp = normalize(resp);
            this.lastPageResponse = pageResp;
            if (direction === 'LAST') {
              this.currentPage.set(this.paginaDeUltima(pageResp, length));
            }
          } else if (
            requestedPage > cur &&
            requestedPage === this.totalPages()
          ) {
            // Última página: direction=LAST con cursor vacío (frontend-girop §17.7); el backend
            // devuelve el resto total % size en una sola petición
            const resp = await this.consultarPagina(null, length, 'LAST');
            pageResp = normalize(resp);
            this.lastPageResponse = pageResp;
            this.currentPage.set(this.paginaDeUltima(pageResp, length));
          } else if (requestedPage > cur) {
            // iterate next until reach requested page
            let resp: any = this.lastPageResponse;
            let currentCursor = resp?.nextCursor ?? null;
            let page = cur;
            while (page < requestedPage) {
              const fetched = await this.consultarPagina(
            currentCursor,
                length,
                'NEXT'
              );
              const norm = normalize(fetched);
              page += 1;
              currentCursor = norm.nextCursor;
              pageResp = norm;
            }
            this.lastPageResponse = pageResp;
            this.currentPage.set(requestedPage);
          } else if (requestedPage < cur) {
            // iterate previous until reach requested page
            let resp: any = this.lastPageResponse;
            let currentCursor = resp?.previousCursor ?? null;
            let page = cur;
            while (page > requestedPage) {
              const fetched = await this.consultarPagina(
            currentCursor,
                length,
                'PREVIOUS'
              );
              const norm = normalize(fetched);
              page -= 1;
              currentCursor = norm.previousCursor;
              pageResp = norm;
            }
            this.lastPageResponse = pageResp;
            this.currentPage.set(requestedPage);
          }
        }

        if (pageResp) {
          items = pageResp.content || [];
          total = pageResp.totalElements ?? (items ? items.length : 0);
        }
      }

      this.contactos.set(items || []);
      this.totalRecords.set(
        typeof total === 'number' ? total : items ? items.length : 0
      );
    } catch (err) {
      this.contactos.set([]);
      this.totalRecords.set(0);
      this.messageService.add({
        severity: 'error',
        summary: 'Contactos',
        detail: 'Error cargando contactos'
      });
    } finally {
      this.loading.set(false);
    }
  }

  /** Número de la última página según el total de la respuesta: ceil(totalElements / size). */
  private paginaDeUltima(
    resp: CursorPageResponse<ContactoPersona>,
    size: number
  ): number {
    return Math.max(1, Math.ceil((resp.totalElements ?? 0) / size));
  }

  async saveContact(
    cdpersona: number,
    editMode: boolean,
    existingId: number | undefined,
    req: any
  ): Promise<ContactoPersona | null> {
    if (this.guardando()) {
      this.ultimoGuardadoExitoso = false;
      return null;
    }
    this.guardando.set(true);
    this.ultimoGuardadoExitoso = false;
    try {
      if (editMode && existingId) {
        await this.repo.update(cdpersona, existingId, req);
        this.messageService.add({
          severity: 'success',
          summary: 'Contacto',
          detail: 'Los datos se actualizaron correctamente.'
        });
        this.ultimoGuardadoExitoso = true;
        // reload
        await this.loadPage(this.first(), this.pageSize());
        return null;
      } else {
        // Si hay nmconsecOrden, usar createFromOrden (legacy: crearContactoCentroTrab con ordenServicioDetalleDto)
        let created: ContactoPersona;
        if (this.params?.nmconsecOrden) {
          created = await this.repo.createFromOrden(
            this.params.nmconsecOrden,
            req
          );
        } else {
          created = await this.repo.create(cdpersona, req);
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Contacto',
          detail: 'Los datos se almacenaron correctamente.'
        });
        this.ultimoGuardadoExitoso = true;
        await this.loadPage(this.first(), this.pageSize());
        return created;
      }
    } catch (error: any) {
      // Try to extract server-provided message (ErrorResponse.detail or message)
      let serverMsg: string | undefined;
      try {
        const body = error?.error;
        if (body) {
          serverMsg = body.detail ?? body.message ?? undefined;
        }
      } catch (e) {
        // ignore
      }

      // Legacy (ListaContactosActivity): ante un fallo se muestra cons.genericError()
      const defaultMsg =
        'Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.';

      // Map HTTP status to severity: validation/conflict -> warn, others -> error
      const status = error?.status;
      const severity = status === 400 || status === 409 ? 'warn' : 'error';

      this.messageService.add({
        severity,
        summary: 'Contacto',
        detail: serverMsg ?? defaultMsg
      });
      return null;
    } finally {
      this.guardando.set(false);
    }
  }
}
